import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Trash2, LogOut, Upload, Play } from "lucide-react";

const API_BASE = 'https://ngpdnb.infaverse.com/api';
const API_URL = `${API_BASE}/notices`;

const Dashboard = () => {
  const [notices, setNotices] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState('');
  const [link, setLink] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [crawlerLogs, setCrawlerLogs] = useState([]);
  const [crawlerRunning, setCrawlerRunning] = useState(false);
  const [crawlerPages, setCrawlerPages] = useState(1);
  const [crawlerLimit, setCrawlerLimit] = useState(10);

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      const res = await axios.get(API_URL);
      setNotices(res.data);
    } catch (err) {
      console.error('❌ Failed to fetch notices:', err);
    }
  };

  const handleImageUpload = (file) => {
    if (file.size > 1024 * 1024) {
      alert('Image size exceeds 1MB. Please upload a smaller image.');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const addNotice = async () => {
    if (!title || !content || !type || !imageFile) {
      alert("Please fill all required fields (Title, Category, Description, Image)");
      return;
    }
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);
      formData.append('type', type);
      formData.append('link', link);
      formData.append('image', imageFile);

      await axios.post(API_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setTitle('');
      setContent('');
      setType('');
      setLink('');
      setImageFile(null);
      setImagePreview(null);

      fetchNotices();
    } catch (err) {
      console.error('❌ Failed to add notice:', err);
    }
  };

  const deleteNotice = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`);
      fetchNotices();
    } catch (err) {
      console.error('❌ Failed to delete notice:', err);
    }
  };

  const logout = () => {
    localStorage.removeItem("isAuthenticated");
    window.location.reload();
  };

  const invokeCrawler = async () => {
    setCrawlerRunning(true);
    try {
      const res = await axios.get(`${API_BASE}/crawler`, {
        params: { pages: crawlerPages, limit: crawlerLimit },
      });
      const { logs = [], message, added, totalEvents } = res.data || {};
      setCrawlerLogs(prev => [...prev, `Run: ${message || 'completed'} (fetched ${totalEvents || 0}, added ${added || 0})`, ...logs]);
      fetchNotices();
    } catch (err) {
      setCrawlerLogs(prev => [...prev, `Crawler run failed: ${err?.message || 'Unknown error'}`]);
    } finally {
      setCrawlerRunning(false);
    }
  };

  return (
    <div className="dashboard">
      <style>{`
        body {
          background: #ffffff;
          margin: 0;
          padding: 0;
          overflow-y: auto;
        }
        .dashboard {
          padding: 30px;
          max-width: 1100px;
          margin: 0 auto;
          font-family: system-ui, sans-serif;
          color: #0f172a;
          min-height: 100vh;
          overflow-y: visible;
        }
        .dashboard__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
        }
        .dashboard__logo {
          max-height: 50px;
          height: auto;
          width: auto;
        }

        @media (max-width: 640px) {
          .dashboard__logo {
            max-height: 35px;
          }
        }
          
        .dashboard__logout {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 18px;
          border: none; 
          background: #ef4444;
          color: #fff;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          transition: background 0.25s ease;
        }
        .dashboard__logout:hover {
          background: #dc2626;
        }

        /* Card container */
        .card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 28px;
          margin-bottom: 24px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }
        .card__title,
        .card__title_add {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 20px;
          color: #111827;
        }

        .card__title_add::before {
          content: "+ ";
          color: #2563eb;
          font-weight: 700;
          margin-right: 4px;
        }

        /* Form styling */
        /* Two-column layout for the form */
        .form__grid {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
        }

        .form__left {
          flex: 1 1 250px; /* minimum width 250px, flexible */
          max-width: 300px;
        }

        .form__right {
          flex: 2 1 400px; /* takes remaining space */
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .form__group {
          display: flex;
          flex-direction: column;
        }
        .form__label {
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 6px;
          color: #374151;
        }
        .form__input,
        .form__textarea {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 0.95rem;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          line-height: 1.4; /* optional for better spacing */
          resize: vertical; /* keep your existing behavior */
          background: #fff;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .form__input:focus,
        .form__textarea:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }
        .form__textarea {
          min-height: 100px;
          resize: vertical;
        }
        .form__upload {
          border: 2px dashed #cbd5e1;
          border-radius: 12px;
          padding: 30px;
          text-align: center;
          cursor: pointer;
          color: #7c8391ff;
          position: relative;
          display: flex;
          margin-top: 5px;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          min-height: 240px;
          background: #f8fafc;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .form__upload:hover {
          border-color: #94a3b8;
          background: #f1f5f9;
        }
        .form__upload p {
          color: #172034ff; /* dark gray / almost black */
          font-weight: 500; /* optional: slightly bolder */
        }
        .form__upload small {
          color: #6b7280;
        }
        .form__upload input {
          position: absolute;
          top: 0; left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
        }
        .form__upload-preview {
          max-width: 100%;
          max-height: 100%;
          border-radius: 8px;
        }
        .form__remove {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.9);
          border: 1px solid #e5e7eb;
          border-radius: 999px;
          cursor: pointer;
        }
        .form__remove:hover {
          background: #fff;
          border-color: #cbd5e1;
        }
        .form__button {
          padding: 12px 24px;
          background: #0f172a;
          width: fit-content;
          color: #fff;
          border: none;
          border-radius: 8px;
          margin-left: auto;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          margin-top: 10px;
          transition: background 0.2s ease, transform 0.1s ease;
        }
        .form__button:hover {
          background: #1e293b;
          transform: translateY(-1px);
        }
        .form__button:active {
          transform: translateY(0);
        }

        /* Notices list */
        .notices__list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .notice__card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 18px;
          display: flex;
          gap: 16px;
          align-items: flex-start;
          transition: box-shadow 0.2s ease, border-color 0.2s ease;
        }
        .notice__card:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          border-color: #cbd5e1;
        }
        .notice__thumb {
          width: 80px;
          height: 80px;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          flex-shrink: 0;
        }
        .notice__body {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .notice__title {
          font-size: 1rem;
          font-weight: 600;
          color: #111827;
        }
        .notice__badge {
          display: inline-block;
          background: #e0f2fe;
          color: #0369a1;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 6px;
          margin-left: 6px;
        }
        .notice__desc {
          font-size: 0.9rem;
          color: #4b5563;
        }
        .notice__link {
          font-size: 0.85rem;
          color: #2563eb;
          text-decoration: underline;
        }
        .notice__delete {
          background: none;
          border: none;
          cursor: pointer;
          color: #ef4444;
          padding: 8px;
          border-radius: 6px;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .notice__delete:hover {
          color: #dc2626;
          background: #fee2e2;
        }

        /* Crawler section */
        .card__header {
          display: flex;
          justify-content: space-between; /* title left, controls right */
          align-items: center;            /* vertical center */
          gap: 12px;
          margin-bottom: 12px;            /* spacing below header */
          flex-wrap: wrap;
        }

        .crawler__controls {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .crawler__input {
          width: 90px;
          padding: 6px 8px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          font-size: 0.9rem;
          background: #fff;
        }

        .crawler__input:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .crawler__logs {
          max-height: 200px;
          overflow-y: auto;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 10px;
          font-size: 0.9rem;
          color: #374151;
          background: #fff;
        }
        .crawler__log {
          padding: 4px 0;
          border-bottom: 1px solid #e5e7eb;
        }
        .crawler__log:last-child {
          border-bottom: none;
        }
        .crawler__button {
          display: inline-flex;       /* align icon + text in a row */
          align-items: center;        /* vertical center */
          gap: 6px;                   /* spacing between icon & text */
          padding: 8px 14px;
          background: #111827;
          color: #fff;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s ease, opacity 0.2s ease;
        }

        .crawler__button:hover {
          background: #1f2937;
        }

        .crawler__button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 640px) {
          .card__header {
            align-items: flex-start;
          }
          .crawler__controls {
            width: 100%;
          }
        }
      `}</style>

      <div className="dashboard__header">
        <img className="dashboard__logo" src="https://ngpdnb.infaverse.com/uploads/admin_logo_black.png" alt="Logo" />
        <button className="dashboard__logout" onClick={logout}>
          Logout <LogOut size={16} />
        </button>
      </div>

      <div className="card">
        <h2 className="card__title_add">Create New Notice</h2>

        <div className="form__grid">
          <div className="form__left">
            <div className="form__group">
              <label className="form__label">Image Upload <span style={{ color: "red" }}>*</span></label>
              <div className="form__upload">
                {!imagePreview ? (
                  <>
                    <Upload size={32} className="form__upload-icon" />
                    <p>Click to upload or drag & drop</p>
                    <small>Max size: 1MB</small>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0])}
                    />
                  </>
                ) : (
                  <>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="form__upload-preview"
                    />
                    <button
                      type="button"
                      className="form__remove"
                      onClick={removeImage}
                    >
                      <X size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="form__right">
            <div className="form__group">
              <label className="form__label">Title <span style={{ color: "red" }}>*</span></label>
              <input
                className="form__input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter notice title"
              />
            </div>

            <div className="form__group">
              <label className="form__label">Category <span style={{ color: "red" }}>*</span></label>
              <input
                className="form__input"
                value={type}
                onChange={(e) => setType(e.target.value)}
                placeholder="e.g., External, Hackathon"
              />
            </div>

            <div className="form__group">
              <label className="form__label">
                Description <span style={{ color: "red" }}>*</span>
              </label>

              <textarea
                className="form__textarea"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter description"
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "6px",
                }}
              >

                <button
                  type="button"
                  style={{
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    border: "1px solid #ffffffff",
                    borderRadius: "6px",
                    background: "#eeeeeeff",
                    color: "#111827",
                    transition: "all 0.2s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "#353535ff";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "#f9fafb";
                    e.currentTarget.style.color = "#353535ff";
                  }}
                  onClick={() => {
                    const textarea = document.querySelector(".form__textarea");
                    const start = textarea.selectionStart;
                    const end = textarea.selectionEnd;
                    if (start !== end) {
                      const selectedText = content.substring(start, end);
                      const newText =
                        content.substring(0, start) +
                        `**${selectedText}**` +
                        content.substring(end);
                      setContent(newText);

                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(start, end + 4);
                      }, 0);
                    }
                  }}
                >
                  B
                </button>

                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                  }}
                >
                  {content.length} / 500
                </div>
              </div>
            </div>

            <div className="form__group">
              <label className="form__label">Link (Optional)</label>
              <input
                className="form__input"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://example.com"
              />
            </div>

            <button className="form__button" onClick={addNotice}>
              + Create Notice
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="card__title">All Notices ({notices.length})</h2>
        <div className="notices__list">
          {notices.map(n => (
            <div key={n.id} className="notice__card">
              {n.image && <img className="notice__thumb" src={n.image} alt="" />}
              <div className="notice__body">
                <span className="notice__title">
                  {n.title}
                  <span className="notice__badge">{n.type}</span>
                </span>
                <p className="notice__desc">{n.content}</p>
                {n.link && <a href={n.link} target="_blank" rel="noreferrer" className="notice__link">View Link</a>}
              </div>
              <button className="notice__delete" onClick={() => deleteNotice(n.id)}><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card__header">
          <h2 className="card__title">Crawler Logs</h2>
          <div className="crawler__controls">
            <input
              type="number"
              min="1"
              value={crawlerPages}
              onChange={(e) => setCrawlerPages(Number(e.target.value) || 1)}
              className="crawler__input"
              aria-label="Pages to crawl"
            />
            <input
              type="number"
              min="1"
              value={crawlerLimit}
              onChange={(e) => setCrawlerLimit(Number(e.target.value) || 1)}
              className="crawler__input"
              aria-label="Max notices to add"
            />
            <button className="crawler__button" onClick={invokeCrawler} disabled={crawlerRunning}>
              <Play size={16} style={{ marginRight: "6px" }} /> {crawlerRunning ? "Running..." : "Run Crawler"}
            </button>
          </div>
        </div>

        <div className="crawler__logs">
          {crawlerLogs.length ? (
            crawlerLogs.map((log, i) => (
              <div key={i} className="crawler__log">
                {log}
              </div>
            ))
          ) : (
            <div className="crawler__log">No logs yet</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
